/**
 * VoiceAuri — the full-screen luxury voice concierge experience.
 * Mounted once from PublicLayout. Opens on the global `altura:talk-to-auri`
 * CustomEvent (also dispatched by the hero "Talk to AURI" button and the
 * text widget's "Switch to voice" affordance).
 *
 * State machine:
 *   hidden → consent → connecting → live (listening/thinking/speaking) → ended
 *
 * If the browser cannot get microphone permission, or the provider is not
 * configured, we gracefully fall back to the existing text `Ask AURI` widget.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";

import { OpenAIRealtimeClient } from "../../lib/voice/OpenAIRealtimeClient";
import VoiceOrb from "./VoiceOrb";
import VoiceTranscript from "./VoiceTranscript";
import VoiceControls from "./VoiceControls";
import VoiceConsentModal from "./VoiceConsentModal";
import VoiceRecommendationCard from "./VoiceRecommendationCard";
import { api } from "../../lib/api";

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

function newSessionKey() {
  return "va-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

function pageContextFromLocation(pathname) {
  if (pathname === "/") return { type: "home", title: "Altura Global — Home" };
  const m1 = pathname.match(/^\/markets\/([a-z0-9_-]+)/i);
  if (m1) return { type: "country", slug: m1[1], title: `${m1[1]} · Altura Global` };
  const m2 = pathname.match(/^\/opportunities\/([a-z0-9_-]+)/i);
  if (m2) return { type: "opportunity", slug: m2[1] };
  const m3 = pathname.match(/^\/learning-center\/([a-z0-9_-]+)/i) || pathname.match(/^\/learning\/([a-z0-9_-]+)/i);
  if (m3) return { type: "article", slug: m3[1] };
  if (pathname === "/explore-ghana") return { type: "country", slug: "ghana", title: "Ghana · Flagship Market" };
  if (pathname === "/opportunities") return { type: "opportunities-index", slug: "opportunities" };
  if (pathname === "/learning-center") return { type: "learning-center", slug: "learning-center" };
  if (pathname === "/resources") return { type: "resources", slug: "resources" };
  if (pathname === "/about") return { type: "page", slug: "about", title: "About Altura" };
  if (pathname === "/contact") return { type: "page", slug: "contact", title: "Contact Altura" };
  if (pathname === "/global-link") return { type: "page", slug: "global-link", title: "Global Link Partners" };
  return { type: "page", slug: pathname };
}

export default function VoiceAuri() {
  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState("hidden"); // hidden | consent | connecting | live | error | ended
  const [state, setState] = useState("idle");   // idle | connecting | listening | thinking | speaking | ready
  const [messages, setMessages] = useState([]);
  const [muted, setMuted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [config, setConfig] = useState(null);
  const [voice] = useState("sage");             // default; A/B preview lives in Command Center
  const [recommendations, setRecommendations] = useState([]);   // active recommendation cards
  const clientRef = useRef(null);
  const location = useLocation();

  // Preload public voice config on mount so the consent modal shows editorial text.
  useEffect(() => {
    api.get("/voice/config").then((r) => setConfig(r.data)).catch(() => setConfig(null));
  }, []);

  // Global opener: any component can dispatch `altura:talk-to-auri`.
  useEffect(() => {
    const handler = () => {
      setVisible(true);
      setPhase("consent");
      setErrorMsg("");
    };
    window.addEventListener("altura:talk-to-auri", handler);
    return () => window.removeEventListener("altura:talk-to-auri", handler);
  }, []);

  // ESC to close
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible]);

  const emitAnalytics = useCallback(async (event, payload) => {
    try {
      await api.post("/voice/analytics", { event, session_key: clientRef.current?.sessionKey || null, payload: payload || {} });
    } catch { /* ignore */ }
  }, []);

  const toolResolvers = {
    altura_search_kb: async ({ query, session_key }) => {
      const r = await fetch(`${BACKEND_BASE}/api/voice/tools/search_kb`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_key, query, max_chars: 4000 }),
      });
      if (!r.ok) return { error: "search_failed" };
      return r.json();
    },
    altura_remember: async (args) => {
      const r = await fetch(`${BACKEND_BASE}/api/voice/tools/remember`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args),
      });
      return r.ok ? r.json() : { error: "remember_failed" };
    },
    altura_recommend: async (args) => {
      const r = await fetch(`${BACKEND_BASE}/api/voice/tools/recommend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args),
      });
      if (!r.ok) return { recommendations: [], error: "reco_failed" };
      const data = await r.json();
      // Show cards immediately (non-blocking; visitor can keep talking)
      if ((data.recommendations || []).length > 0) {
        setRecommendations((prev) => {
          const seen = new Set(prev.map((x) => x.id));
          const merged = [...prev];
          for (const rec of data.recommendations) {
            if (!seen.has(rec.id)) merged.push(rec);
          }
          return merged.slice(-3);
        });
      }
      return data;
    },
    altura_capture_lead: async (args) => {
      const r = await fetch(`${BACKEND_BASE}/api/voice/tools/capture_lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...args, consent_accepted: true }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        return { error: "capture_failed", message: j.detail || "Could not save contact." };
      }
      return r.json();
    },
    altura_request_handoff: async ({ reason, urgency, session_key }) => {
      const r = await fetch(`${BACKEND_BASE}/api/voice/tools/request_handoff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason, urgency, session_key }),
      });
      return r.ok ? r.json() : { error: "handoff_failed" };
    },
  };

  const startConversation = async () => {
    setPhase("connecting");
    setState("connecting");
    setMessages([]);
    setErrorMsg("");
    emitAnalytics("consent_accepted");

    const client = new OpenAIRealtimeClient({ backendBaseUrl: BACKEND_BASE, toolResolvers });
    clientRef.current = client;

    // Wire events
    client.on("state", ({ state: s }) => setState(s));
    client.on("connected", () => { setPhase("live"); emitAnalytics("connected"); });
    client.on("transcript", ({ role, text, final }) => {
      setMessages((prev) => {
        const list = [...prev];
        // Assistant streams — collapse deltas into a single trailing row.
        if (role === "assistant" && !final) {
          const last = list[list.length - 1];
          if (last && last.role === "assistant" && !last.final) {
            last.text = (last.text || "") + (text || "");
            return [...list.slice(0, -1), last];
          }
          list.push({ role, text: text || "", final: false });
          return list;
        }
        if (role === "assistant" && final) {
          const last = list[list.length - 1];
          if (last && last.role === "assistant" && !last.final) {
            list[list.length - 1] = { role, text: text || last.text, final: true };
            return list;
          }
          list.push({ role, text: text || "", final: true });
          return list;
        }
        // user turn
        list.push({ role: "user", text: text || "", final: true });
        return list;
      });
    });
    client.on("interruption", () => emitAnalytics("interruption"));
    client.on("error", ({ message }) => {
      setErrorMsg(message || "Voice error");
      emitAnalytics("error", { message: (message || "").slice(0, 200) });
    });
    client.on("ended", () => { setPhase("ended"); emitAnalytics("ended_ok"); });

    try {
      await client.connect({
        sessionKey: newSessionKey(),
        consent: true,
        voice,
        pageContext: pageContextFromLocation(location.pathname),
        sourcePage: location.pathname,
      });
    } catch (e) {
      const msg = String(e?.message || e);
      setErrorMsg(msg);
      setPhase("error");
      emitAnalytics("connect_error", { message: msg.slice(0, 200) });
    }
  };

  const close = () => {
    (async () => {
      try { await clientRef.current?.end(); } catch { /* ignore */ }
      clientRef.current = null;
    })();
    setVisible(false);
    setPhase("hidden");
    setState("idle");
    setMessages([]);
    setRecommendations([]);
  };

  const switchToText = () => {
    // Close voice, open text widget with a hint prompt so nothing is lost.
    (async () => { try { await clientRef.current?.end(); } catch { /* ignore */ } })();
    clientRef.current = null;
    setVisible(false);
    setPhase("hidden");
    setState("idle");
    // dispatch the existing text-mode opener
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("altura:ask-auri", { detail: { prompt: "" } }));
    }, 60);
    emitAnalytics("fallback_to_text");
  };

  const toggleMute = async () => {
    if (!clientRef.current) return;
    if (muted) { await clientRef.current.unmute(); setMuted(false); }
    else       { await clientRef.current.mute();   setMuted(true); }
  };

  if (!visible) return null;

  return (
    <div className="voice-auri" data-testid="voice-auri" role="dialog" aria-modal="true">
      <div className="voice-auri__bg" aria-hidden="true" />
      <div className="voice-auri__grain" aria-hidden="true" />

      {phase === "consent" && (
        <VoiceConsentModal
          consentText={config?.consent_text || "Voice AURI uses your microphone to process spoken questions."}
          disclaimerText={config?.disclaimer_text}
          onAccept={startConversation}
          onSwitchToText={switchToText}
          onClose={close}
        />
      )}

      {phase !== "consent" && (
        <div className="voice-auri__stage">
          <button
            type="button"
            className="voice-auri__close"
            onClick={close}
            aria-label="Close Voice AURI"
            data-testid="voice-close"
          >
            ×
          </button>

          <div className="voice-auri__brand">
            <div className="voice-auri__brand-mark">
              <Sparkles className="h-3.5 w-3.5 text-altura-gold" />
              <span>AURI</span>
            </div>
            <div className="voice-auri__brand-title">Your Global Investment Concierge</div>
            <div className="voice-auri__brand-sub">Powered by AURELIS Intelligence</div>
          </div>

          <div className="voice-auri__center">
            <VoiceOrb state={state} size={260} />
            <div className="voice-auri__state" data-testid="voice-state">
              {phase === "connecting" && "Connecting…"}
              {phase === "live" && {
                idle: "Ready — say hello.",
                listening: "Listening…",
                thinking: "Thinking…",
                speaking: "Speaking…",
                ready: "Say something whenever you're ready.",
                connecting: "Connecting…",
              }[state]}
              {phase === "error" && (errorMsg || "Voice couldn't start.")}
              {phase === "ended" && "Conversation ended."}
            </div>

            {phase === "error" && (
              <div className="voice-auri__error">
                <p>{errorMsg}</p>
                <button className="btn-outline-gold btn-compact" onClick={switchToText}>Continue in Text</button>
              </div>
            )}
          </div>

          <VoiceTranscript messages={messages} state={state} />

          {recommendations.length > 0 && (
            <div className="voice-reco__list" data-testid="voice-reco-list">
              {recommendations.map((rec) => (
                <VoiceRecommendationCard
                  key={rec.id}
                  item={rec}
                  onOutcome={async (outcome, item) => {
                    try {
                      await fetch(`${BACKEND_BASE}/api/voice/tools/recommendation_outcome`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ session_key: clientRef.current?.sessionKey, recommendation_id: item.id, outcome }),
                      });
                    } catch (e) { void e; }
                  }}
                  onView={() => { /* href handles nav */ }}
                  onAsk={(item) => { clientRef.current?.sendText?.(`Tell me more about ${item.title}.`); }}
                  onRequestInfo={(item) => { window.open("/contact", "_blank"); void item; }}
                  onDismiss={(item) => setRecommendations((prev) => prev.filter((x) => x.id !== item.id))}
                />
              ))}
            </div>
          )}

          <VoiceControls
            muted={muted}
            state={state}
            disabled={phase !== "live"}
            onToggleMute={toggleMute}
            onSwitchToText={switchToText}
            onEnd={close}
          />
        </div>
      )}
    </div>
  );
}
